// Ad attribution capture — the spine of the tracking plan (.claude/TRACKING-PLAN.md).
//
// On app boot we capture UTM params, click IDs (gclid/gbraid/wbraid/fbclid),
// the Meta _fbp cookie, referrer, and landing path, then persist them in
// localStorage under `vovy_attribution_v1` as { first, last }:
//   • first — the very first touch this browser ever saw. NEVER overwritten.
//   • last  — overwritten whenever a new visit carries any param or referrer.
// Both are registered as Mixpanel super props (last-touch) and people
// $set_once (first-touch), written to public.users.attribution on every
// authenticated session (persistAttribution, called from useAuthService's
// onAuthStateChange chokepoint + AuthCallback), and carried into the desktop
// app via vovy:// deep links (Welcome.tsx / Open.tsx → attributionQueryString()).

import mixpanel from '@/utils/mixpanel';
import { supabase } from '@/integrations/supabase/client';

const STORAGE_KEY = 'vovy_attribution_v1';
/** auth_user_ids whose `users.attribution` we've already settled in this browser. */
const SYNCED_KEY = 'vovy_attribution_synced_v1';

const PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'twclid',
  'gbraid',
  'wbraid',
  'fbclid',
] as const;

export interface AttributionTouch {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  twclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  /** Meta click cookie format: fb.1.<ts>.<fbclid> — what CAPI wants as fbc. */
  fbc?: string;
  /** Meta browser cookie (_fbp), read as-is. */
  fbp?: string;
  referrer?: string;
  landing_page?: string;
  ts?: string;
}

export interface StoredAttribution {
  first?: AttributionTouch;
  last?: AttributionTouch;
}

const readCookie = (name: string): string | undefined => {
  try {
    const match = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith(`${name}=`));
    return match ? decodeURIComponent(match.slice(name.length + 1)) : undefined;
  } catch {
    return undefined;
  }
};

const readStored = (): StoredAttribution => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredAttribution) : {};
  } catch {
    return {};
  }
};

const writeStored = (value: StoredAttribution) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* private mode / storage disabled — attribution simply won't persist */
  }
};

/**
 * Paths a visit never starts on: the auth provider's redirect target, SSO
 * callbacks and the checkout return page. The browser lands there at the
 * tail of a redirect, so the path is not a landing page and the referrer
 * (accounts.google.com, checkout.stripe.com) is not a touch. Live Stripe
 * showed attr_landing_page "/auth/callback" on a web checkout: the OAuth
 * return, with Google as an "external referrer", had overwritten the real
 * last touch. Mirrors isInternalRedirectPath in
 * supabase/functions/_shared/stripeAttribution.ts, which drops it server-side too.
 */
export const isInternalRedirectPath = (path: string | null | undefined): boolean => {
  if (typeof path !== 'string') return false;
  const p = path.trim().toLowerCase();
  return /^\/(auth|oauth|sso|workos|callback)(\/|\?|#|$)/.test(p)
    || /^\/(extension-checkout-return|github\/callback|github-oauth-callback)(\/|\?|#|$)/.test(p);
};

/** Referrers that are a hop in our own flows, never a source: identity
 *  providers, payment pages and our own backends. */
const REDIRECT_REFERRER_HOSTS = /(^|\.)(accounts\.google\.com|appleid\.apple\.com|login\.microsoftonline\.com|github\.com|api\.workos\.com|auth\.workos\.com|checkout\.stripe\.com|billing\.stripe\.com|supabase\.co|supabase\.in|lovable\.app)$/i;
export const isRedirectReferrer = (ref: string | null | undefined): boolean => {
  if (!ref) return false;
  try {
    return REDIRECT_REFERRER_HOSTS.test(new URL(ref).hostname);
  } catch {
    return false;
  }
};

/** Build a touch from the current URL/cookies/referrer. */
const captureTouch = (): AttributionTouch => {
  const touch: AttributionTouch = {};
  try {
    const params = new URLSearchParams(window.location.search);
    for (const key of PARAM_KEYS) {
      const value = params.get(key);
      if (value) touch[key] = value;
    }
    if (touch.fbclid) touch.fbc = `fb.1.${Date.now()}.${touch.fbclid}`;
    const fbp = readCookie('_fbp');
    if (fbp) touch.fbp = fbp;
    const redirectLanding = isInternalRedirectPath(window.location.pathname);
    // Ignore same-site referrers (internal navigation isn't a touch) and the
    // identity/payment providers that bounce the browser back to us.
    const ref = document.referrer;
    if (ref && !ref.startsWith(window.location.origin) && !isRedirectReferrer(ref) && !redirectLanding) touch.referrer = ref;
    // The landing page is the first page of the visit, never a redirect target.
    if (!redirectLanding) touch.landing_page = window.location.pathname;
    touch.ts = new Date().toISOString();
  } catch {
    /* attribution must never break the app */
  }
  return touch;
};

/** A touch is "meaningful" if it carries any ad param or external referrer. */
const hasSignal = (touch: AttributionTouch): boolean =>
  PARAM_KEYS.some((k) => Boolean(touch[k])) || Boolean(touch.referrer);

/**
 * Capture attribution from the current URL. Call once at app boot (App.tsx,
 * next to capturePromoSlug). Idempotent: first-touch is only ever written
 * once; last-touch only updates when the visit actually carries signal.
 */
export const captureAttribution = () => {
  if (typeof window === 'undefined') return;
  try {
    const stored = readStored();
    const touch = captureTouch();
    let dirty = false;

    if (!stored.first) {
      stored.first = touch;
      dirty = true;
    }
    if (hasSignal(touch)) {
      stored.last = touch;
      dirty = true;
    } else if (!stored.last) {
      stored.last = touch;
      dirty = true;
    }
    if (dirty) writeStored(stored);

    // The PostHog adapter queues these calls before initialization.
    const register = () => {
      const flat = getAttributionParams();
      if (Object.keys(flat).length) mixpanel.register(flat);
      if (stored.first) {
        const firstProps: Record<string, string> = {};
        for (const [k, v] of Object.entries(stored.first)) {
          if (typeof v === 'string' && k !== 'ts') firstProps[`first_${k}`] = v;
        }
        if (Object.keys(firstProps).length) mixpanel.people.set_once(firstProps);
      }
    };
    register();
  } catch {
    /* attribution must never break the app */
  }
};

/** The raw stored { first, last } object — what gets persisted to the user row. */
export const getAttribution = (): StoredAttribution => readStored();

/**
 * The two Meta CAPI browser identifiers, for server-side events that must match
 * a browser pixel hit (see supabase/functions/meta-capi-download).
 *   • fbp — read LIVE from the _fbp cookie, falling back to whatever the stored
 *     touch captured (the cookie is the source of truth; storage covers the
 *     case where the pixel set it after our capture).
 *   • fbc — the _fbc cookie if Meta set one, else the value we derived from the
 *     stored fbclid at capture time.
 * Returns '' (never undefined) for anything unavailable, so callers can send
 * the fields unconditionally. Never throws.
 */
export const getMetaCookies = (): { fbp: string; fbc: string } => {
  try {
    const flat = getAttributionParams();
    return {
      fbp: readCookie('_fbp') || flat.fbp || '',
      fbc: readCookie('_fbc') || flat.fbc || '',
    };
  } catch {
    return { fbp: '', fbc: '' };
  }
};



const readSynced = (): string[] => {
  try {
    const raw = localStorage.getItem(SYNCED_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
};

const markSynced = (authUserId: string) => {
  try {
    const list = readSynced();
    if (list.includes(authUserId)) return;
    // Keep the tail only — a shared browser never accumulates more than a few.
    localStorage.setItem(SYNCED_KEY, JSON.stringify([...list, authUserId].slice(-5)));
  } catch {
    /* storage disabled — we'll just re-check the row next session */
  }
};

/** In-flight guard so the two call sites in one page load don't double-query. */
const persistInFlight = new Set<string>();

/**
 * The auth user id last seen at the universal chokepoint (persistAttribution,
 * called from useAuthService's onAuthStateChange + AuthCallback). Lets
 * downloadQueryString() stamp a download click with the signed-in user without
 * an async Supabase round-trip inside a click handler. Null when signed out.
 */
let lastAuthUserId: string | null = null;

/**
 * Persist this browser's ad attribution ({ first, last }) onto the user's row
 * so server-side conversions (stripe-webhook-inc → Meta CAPI, Google Ads
 * offline import) can use it.
 *
 * Called from the universal chokepoint — useAuthService's onAuthStateChange,
 * which fires SIGNED_IN/INITIAL_SESSION for every authenticated path (email
 * signup, OAuth popup, OAuth redirect, session restore) — and again from
 * AuthCallback right after it inserts the users row, so a brand-new SSO signup
 * doesn't have to wait for the next session to be attributed.
 *
 * • First-touch wins: only ever writes when `attribution` is still null, both
 *   in the read check and as an `.is('attribution', null)` update filter.
 * • Cheap to repeat: a localStorage marker short-circuits every later call for
 *   an already-settled user, so session restores cost zero DB round-trips.
 * • Fire-and-forget: fully async, all errors swallowed — never blocks or
 *   breaks the auth flow.
 */
export const persistAttribution = (authUserId?: string | null) => {
  if (typeof window === 'undefined' || !authUserId) return;
  // Remember who's signed in for downloadQueryString(). Deliberately set here —
  // above the in-flight/already-synced short-circuit below — because the common
  // case (a returning user whose row was settled sessions ago) returns early on
  // that line, and we still need their id on a download click.
  lastAuthUserId = authUserId;
  if (persistInFlight.has(authUserId) || readSynced().includes(authUserId)) return;
  persistInFlight.add(authUserId);
  void (async () => {
    try {
      const attribution = readStored();
      if (!attribution.first && !attribution.last) return;

      const { data: row } = (await supabase
        .from('users' as any)
        .select('attribution')
        .eq('auth_user_id', authUserId)
        .maybeSingle()) as { data: { attribution?: unknown } | null };

      // Row not created yet (some signup paths insert it just after auth) —
      // don't mark synced, so the next session restore retries.
      if (!row) return;
      if (row.attribution != null) {
        markSynced(authUserId);
        return;
      }

      await supabase
        .from('users' as any)
        .update({ attribution } as any)
        .eq('auth_user_id', authUserId)
        .is('attribution', null);
      markSynced(authUserId);
    } catch {
      /* attribution must never break the auth flow */
    } finally {
      persistInFlight.delete(authUserId);
    }
  })();
};

/**
 * Flat params for analytics/checkout payloads: last-touch merged over
 * first-touch (last wins), timestamps dropped.
 */
export const getAttributionParams = (): Record<string, string> => {
  const { first, last } = readStored();
  const out: Record<string, string> = {};
  for (const touch of [first, last]) {
    if (!touch) continue;
    for (const [k, v] of Object.entries(touch)) {
      if (typeof v === 'string' && k !== 'ts') out[k] = v;
    }
  }
  return out;
};

/**
 * Keys worth carrying across the vovy:// deep-link boundary — the full ad set
 * the desktop app needs to report a conversion: every click ID (Google's
 * gclid/gbraid/wbraid, Meta's fbclid) plus the two Meta CAPI cookies (fbc/fbp),
 * which can't be re-derived on the desktop side. Deliberately excludes
 * referrer/landing_page: long, URL-encodes badly, and not needed downstream.
 */
const DEEPLINK_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'fbc',
  'fbp',
] as const;

/**
 * URL-encoded `utm_*` + click-ID + Meta-cookie subset for appending to deep
 * links (`vovy://auth/handoff?token=…&<this>`), plus `ph_did` (the web PostHog
 * distinct id, see webDistinctId). Keys are the canonical param names (gclid,
 * gbraid, wbraid, fbclid, fbc, fbp, ph_did) so the desktop side can read them
 * straight through. Empty values are omitted; returns an empty string when
 * nothing was captured, so callers can append unconditionally.
 */
export const attributionQueryString = (): string => {
  try {
    const flat = getAttributionParams();
    const qs = new URLSearchParams();
    for (const key of DEEPLINK_KEYS) {
      if (flat[key]) qs.set(key, flat[key]);
    }
    const phDid = webDistinctId();
    if (phDid) qs.set(PH_DID_PARAM, phDid);
    return qs.toString();
  } catch {
    return '';
  }
};

/**
 * `ph_did`: the web PostHog distinct id, carried on every vovy:// deep link so
 * the desktop app can merge the web person (its pre-signup pageviews, the
 * download click, the ad touch) into the account it identifies. The desktop
 * accepts `^[A-Za-z0-9._:-]{8,100}$` after trim (vovy-desktop handoff.ts);
 * the same rule is applied here so a junk value never rides the link, and the
 * server allow-list (vcodinator _shared/attribution.ts) matches it again.
 */
export const PH_DID_PARAM = 'ph_did';
const PH_DID_RE = /^[A-Za-z0-9._:-]{8,100}$/;
export const webDistinctId = (): string | undefined => {
  try {
    const id = mixpanel.get_distinct_id();
    return typeof id === 'string' && PH_DID_RE.test(id.trim()) ? id.trim() : undefined;
  } catch {
    return undefined;
  }
};

/**
 * `attributionQueryString()` plus the three identity/context params the
 * `desktop-download` edge fn accepts, for appending to MACOS_DOWNLOAD_URL:
 *
 *   • distinct_id  — the Mixpanel visitor id, so the download joins the same
 *                    person's web session. Only sent when it's a plausible id
 *                    (>= 8 chars); the edge fn applies the same MIN_ID_LEN
 *                    floor, and a junk value would poison the join.
 *   • auth_user_id — the signed-in user, when known (see lastAuthUserId).
 *   • placement    — which surface the click came from ("pricing", "hero", …).
 *
 * Every part is optional; returns '' when nothing is known, so callers can
 * append unconditionally. Never throws — this runs inside a click handler that
 * must not be able to block a download.
 */
export const downloadQueryString = (placement?: string): string => {
  try {
    const qs = new URLSearchParams(attributionQueryString());
    const distinctId = mixpanel.get_distinct_id();
    if (typeof distinctId === 'string' && distinctId.length >= 8) {
      qs.set('distinct_id', distinctId);
    }
    if (lastAuthUserId) qs.set('auth_user_id', lastAuthUserId);
    if (placement) qs.set('placement', placement);
    return qs.toString();
  } catch {
    return '';
  }
};
